'use client'

import { useEffect, useRef, useState } from 'react'

interface OtpTimerProps {
  expiresAt?: Date | null
  onExpire?: () => void
}

/**
 * Seconds left until `expiresAt`.
 *
 * `ceil` rather than `floor`: the OTP window is two minutes, and by the time
 * the response has crossed the network a floor would already read 1:59 — the
 * countdown is supposed to start at the number the user was promised. It still
 * reaches exactly 0 at expiry, since anything at or past the deadline clamps.
 */
function secondsUntil(expiresAt?: Date | null): number {
  if (!expiresAt) return 0
  const deadline = expiresAt.getTime()
  // A malformed timestamp yields NaN, and NaN comparisons are all false —
  // which would leave the timer rendering "NaN:NaN" forever.
  if (!Number.isFinite(deadline)) return 0
  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
}

export default function OtpTimer({ expiresAt, onExpire }: OtpTimerProps) {
  // Seeded during render, not from an effect. Effects run after paint, so the
  // initial `useState(0)` meant every user saw 0:00 first and the real value
  // only a frame later — the bug this component is named for.
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    secondsUntil(expiresAt),
  )

  // Re-seed the same way when a resend hands over a new deadline. React's
  // "adjust state on prop change" pattern: it costs an extra render but never
  // shows the previous code's countdown against the new code.
  const deadline = expiresAt?.getTime() ?? null
  const [lastDeadline, setLastDeadline] = useState(deadline)
  if (deadline !== lastDeadline) {
    setLastDeadline(deadline)
    setRemainingSeconds(secondsUntil(expiresAt))
  }

  // Held in a ref so an inline `onExpire={() => …}` from the parent — which is
  // a new function on every keystroke in the OTP inputs — cannot tear down and
  // rebuild the interval, resetting its one-second cadence each time.
  const onExpireRef = useRef(onExpire)
  onExpireRef.current = onExpire

  useEffect(() => {
    if (!deadline) {
      setRemainingSeconds(0)
      return
    }

    let fired = false
    const tick = () => {
      const next = secondsUntil(new Date(deadline))
      setRemainingSeconds(next)
      if (next === 0 && !fired) {
        fired = true
        onExpireRef.current?.()
      }
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [deadline])

  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60

  return (
    <span className="text-body-md font-medium text-muted-foreground tabular-nums">
      {minutes}:{seconds.toString().padStart(2, '0')}
    </span>
  )
}
